import { endOfDay } from 'date-fns';
import { TQuoter } from '../../../types';
import { dateToPlainDateString, TemporalPlainDateFromJsDate } from '../../../util/date-utilts';
import { FMT } from '../../../util/formatter-service';
import { getFiscalQuarter } from '../../../util/formatters/DateFormatters';

const MIN_AXIS_QUARTERS = 12;
const MAX_AXIS_QUARTERS = 120;

// implementation assumes fye is always December
export class QuartersBuilder {
  private static getQuarterEndDate(quarter: number, year: number): Date {
    if (quarter === 3) {
      return new Date(`01/01/${year + 1} 23:59:59`);
    }

    const qEnd = new Date((quarter + 1) * 3 + 1 + '/01/' + year);
    qEnd.setHours(23);
    qEnd.setMinutes(59);
    qEnd.setSeconds(59);

    return qEnd;
  }

  static getPreviousQuarterEnd(today = new Date()) {
    const quarter = Math.floor(today.getMonth() / 3);
    const startFullQuarter = new Date(today.getFullYear(), quarter * 3 - 3, 1);
    return new Date(startFullQuarter.getFullYear(), startFullQuarter.getMonth() + 3, 0);
  }

  private static isQuarterEnd(today: Date) {
    return getFiscalQuarter(today, 12).plainDate.equals(TemporalPlainDateFromJsDate(today));
  }

  private static getLastFullQuarterEnd(today = new Date()) {
    if (QuartersBuilder.isQuarterEnd(today)) {
      return endOfDay(today);
    }
    return QuartersBuilder.getPreviousQuarterEnd(today);
  }

  // The as-of date as a trailing partial quarter, labeled with its literal date; null when
  // the as-of date is a quarter end, since that quarter is then already the last full one.
  private static asOfQuarter(today: Date): TQuoter | null {
    if (QuartersBuilder.isQuarterEnd(today)) {
      return null;
    }

    return {
      // Built from the plain date so the label matches the local day in every timezone.
      label: FMT.format('dateNumeric', dateToPlainDateString(today)),
      start: new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1),
      end: endOfDay(today),
    };
  }

  private static withAsOfPoint(quarters: TQuoter[], today: Date): TQuoter[] {
    const asOf = QuartersBuilder.asOfQuarter(today);

    return asOf ? [asOf, ...quarters] : quarters;
  }

  private static getQuarter(date: Date, id: number): TQuoter {
    // quarter is 0-based here
    let quarter = Math.floor(date.getMonth() / 3);
    let year = date.getFullYear();
    quarter -= id;

    if (quarter < 0) {
      const yearsChanged = Math.ceil(-quarter / 4);
      year -= yearsChanged;
      // Shift quarter back to a nonnegative number
      quarter += 4 * yearsChanged;
    }

    const start = new Date(quarter * 3 + 1 + '/01/' + year);
    start.setHours(0);
    start.setMinutes(0);
    start.setSeconds(0);

    const end = QuartersBuilder.getQuarterEndDate(quarter, year);

    return {
      label: 'Q' + (quarter + 1) + ' ' + year,
      start,
      end,
    };
  }

  // The last `count` full quarters, most recent first.
  private static lastFullQuarters(count: number, today: Date): TQuoter[] {
    const d = QuartersBuilder.getLastFullQuarterEnd(today);

    const dates: TQuoter[] = [];
    for (let i = 0; i < count; i += 1) {
      dates.push(QuartersBuilder.getQuarter(d, i));
    }

    return dates;
  }

  // The last four full quarters plus the as-of point (see asOfQuarter), most recent first.
  static lastFourQuarters(today = new Date()): TQuoter[] {
    return QuartersBuilder.withAsOfPoint(QuartersBuilder.lastFullQuarters(4, today), today);
  }

  // The last twelve full quarters plus the as-of point (see asOfQuarter), most recent first.
  static last12Quarters(today = new Date()): TQuoter[] {
    return QuartersBuilder.withAsOfPoint(QuartersBuilder.lastFullQuarters(12, today), today);
  }

  // Axis for the over-time charts: full history from the earliest data point, never
  // shorter than the 12-quarter window those charts used to show, and capped so a
  // wrong-but-parseable historical date (e.g. a year typo) cannot stretch it forever.
  // Ends at the as-of point, so the current partial quarter is drawn through the as-of date.
  // Callers must fold anything older than the first quarter into it — see the charts.
  static axisQuartersSince(earliest: Date | null, today = new Date()): TQuoter[] {
    const full = earliest ? QuartersBuilder.quartersSince(earliest, today, MAX_AXIS_QUARTERS) : [];

    return full.length >= MIN_AXIS_QUARTERS
      ? QuartersBuilder.withAsOfPoint(full, today)
      : QuartersBuilder.last12Quarters(today);
  }

  // Quarters from the quarter containing `from` up to the last full quarter, most recent
  // first, at most `max` of them. Empty when `from` is later than the last full quarter.
  static quartersSince(from: Date, today = new Date(), max = Infinity): TQuoter[] {
    const d = QuartersBuilder.getLastFullQuarterEnd(today);
    const span =
      (d.getFullYear() - from.getFullYear()) * 4 +
      Math.floor(d.getMonth() / 3) -
      Math.floor(from.getMonth() / 3) +
      1;
    // Capped before the loop so a year typo can't cost thousands of Date allocations.
    const count = Math.min(span, max);

    const dates: TQuoter[] = [];
    for (let i = 0; i < count; i += 1) {
      dates.push(QuartersBuilder.getQuarter(d, i));
    }

    return dates;
  }
}
